import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import IdfFileDropzone from 'components/IdfFileDropzone';
import UseAuthContext from 'context/AuthContext';
import { useFormik } from 'formik';
import {
  CircleCheck,
  Clock,
  Coins,
  Hourglass,
  Play,
  Sparkles,
  Wrench,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { analyzePublicIdf, PublicAnalyzeResponse } from 'services/publicAnalyze';
import {
  climate_v3_options,
  terrain_v3_options,
  type_options,
} from 'utils/Formatter';
import {
  clearIdf,
  loadDraft,
  PUBLIC_ANALYZE_MAX_IDF_BYTES,
  saveIdf,
  saveMeta,
} from 'utils/publicAnalyzeDraft';
import { getErrorHtmlContent } from 'utils/utils';
import * as Yup from 'yup';
import PaSelect from './PaSelect';
import './PublicAnalyzePage.css';

const RATE_DEFAULT = 120;
const RATE_MIN = 60;
const RATE_MAX = 300;

const ANALYZE_STEPS = [
  'PUBLIC_ANALYZE_STEP_PARSE',
  'PUBLIC_ANALYZE_STEP_CODE',
  'PUBLIC_ANALYZE_STEP_COMPARE',
  'PUBLIC_ANALYZE_STEP_CLASSIFY',
] as const;

const formatHours = (mins: number) =>
  Math.round((Math.max(0, mins) / 60) * 10) / 10;

/** Keep ₪ before the number in RTL (bidi would otherwise flip to "180 ₪"). */
const IlsAmount = ({
  value,
  className,
}: {
  value: number;
  className?: string;
}) => (
  <span className={className} dir="ltr">
    <span className="ils-symbol" aria-hidden>
      ₪
    </span>
    {' '}
    {Math.round(Math.max(0, value)).toLocaleString('en-US')}
  </span>
);

/** Has hard mismatches → overwrite. Clean (verification-only / height) → skip overwrite. */
const skipOverwriteFromDiagnosis = (result: PublicAnalyzeResponse) =>
  (result.summary?.hardMismatches ?? result.summary?.mismatches ?? 0) === 0;

/** Same split as diagnosis: red = mismatch; orange / missing code = verification required. */
const isHardMismatch = (row: {
  isRed?: boolean;
  hasNullCodeValue?: boolean;
  severityType?: 'null' | 'orange' | 'red';
  codeValue?: string | null;
  section?: string;
  field?: string;
}) => {
  if (row.section === 'Geometry' && row.field === 'Height (m)') return false;
  if (row.isRed) return true;
  const codeValueEmpty =
    row.codeValue === null ||
    row.codeValue === undefined ||
    row.codeValue === '' ||
    row.codeValue === 'Verification required';
  const isVerificationRequired =
    row.hasNullCodeValue === true ||
    row.severityType === 'orange' ||
    codeValueEmpty;
  return !isVerificationRequired;
};

const PublicAnalyzePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated } = useContext(UseAuthContext);

  const [file, setFile] = useState<File | null>(null);
  const [fileMissing, setFileMissing] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzePct, setAnalyzePct] = useState(0);
  const [analyzeStepIdx, setAnalyzeStepIdx] = useState(0);
  const [result, setResult] = useState<PublicAnalyzeResponse | null>(null);
  const [hourlyRate, setHourlyRate] = useState(RATE_DEFAULT);

  const formik = useFormik({
    initialValues: {
      type: 'Residential',
      climate: '',
      terrain: 'city',
      singleBlockPerFloor: true,
    },
    validationSchema: Yup.object({
      type: Yup.string().required(t('FIELDS_REQUIRED')),
      climate: Yup.string().required(t('FIELDS_REQUIRED')),
      terrain: Yup.string().required(t('FIELDS_REQUIRED')),
    }),
    onSubmit: () => undefined,
  });

  const { type, climate, terrain, singleBlockPerFloor } = formik.values;
  const fieldError = (name: 'type' | 'climate' | 'terrain') =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const draft = await loadDraft();
      if (cancelled || !draft.meta) return;
      formik.setValues({
        type: draft.meta.type || 'Residential',
        climate: draft.meta.climate || '',
        terrain: draft.meta.terrain || 'city',
        singleBlockPerFloor: draft.meta.single_block_per_floor !== false,
      });
      if (draft.file) setFile(draft.file);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Same cadence as portable HTML: animate to 100% in parallel with the API (do not stall at 92%). */
  const playAnalyzeAnimation = () =>
    new Promise<void>((resolve) => {
      const steps = ANALYZE_STEPS.length;
      const timers: number[] = [];
      let delay = 0;
      setAnalyzePct(0);
      setAnalyzeStepIdx(0);
      for (let i = 0; i < steps; i++) {
        const step = i;
        delay += 320;
        timers.push(
          window.setTimeout(() => {
            setAnalyzePct(Math.round((step / steps) * 92));
            setAnalyzeStepIdx(step);
          }, delay)
        );
        delay += 140;
        timers.push(
          window.setTimeout(() => {
            setAnalyzePct(Math.round(((step + 1) / steps) * 92));
            setAnalyzeStepIdx(step + 1);
          }, delay)
        );
      }
      timers.push(
        window.setTimeout(() => {
          setAnalyzePct(100);
          setAnalyzeStepIdx(steps);
          resolve();
        }, delay + 200)
      );
    });

  const saveSettingsSlice = (patch: {
    type?: string;
    climate?: string;
    terrain?: string;
    single_block_per_floor?: boolean;
    skip_overwrite?: boolean;
    pendingAction?: 'createAndRun' | null;
    analyzeSummary?: PublicAnalyzeResponse['summary'] & {
      timeImpact?: PublicAnalyzeResponse['timeImpact'];
    } | null;
  }) => {
    saveMeta({
      type: patch.type ?? type,
      climate: patch.climate ?? climate,
      terrain: patch.terrain ?? terrain,
      single_block_per_floor:
        patch.single_block_per_floor ?? singleBlockPerFloor,
      skip_overwrite: patch.skip_overwrite ?? true,
      pendingAction:
        patch.pendingAction !== undefined ? patch.pendingAction : null,
      version: '4.x.x',
      idfFileName: file?.name,
      analyzeSummary:
        patch.analyzeSummary !== undefined
          ? patch.analyzeSummary
          : result?.summary
            ? { ...result.summary, timeImpact: result.timeImpact || undefined }
            : null,
    });
  };

  const acceptFile = async (next: File | null) => {
    if (!next) return;
    if (next.size > PUBLIC_ANALYZE_MAX_IDF_BYTES) {
      enqueueSnackbar(t('PUBLIC_ANALYZE_IDF_TOO_LARGE'), { variant: 'error' });
      return;
    }
    if (!/\.idf$/i.test(next.name)) {
      enqueueSnackbar(t('PUBLIC_ANALYZE_NEED_IDF'), { variant: 'warning' });
      return;
    }
    setFile(next);
    setFileMissing(false);
    setResult(null);
    try {
      await saveIdf(next);
      saveSettingsSlice({ skip_overwrite: true, pendingAction: null, analyzeSummary: null });
    } catch {
      enqueueSnackbar(t('PUBLIC_ANALYZE_DRAFT_SAVE_FAILED'), { variant: 'error' });
    }
  };

  const clearFile = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setFile(null);
    setFileMissing(false);
    setResult(null);
    void clearIdf();
  };

  const handleAnalyze = async () => {
    if (!file) {
      setFileMissing(true);
      enqueueSnackbar(t('PUBLIC_ANALYZE_NEED_IDF'), { variant: 'warning' });
      return;
    }
    const errors = await formik.validateForm();
    formik.setTouched({
      type: true,
      climate: true,
      terrain: true,
      singleBlockPerFloor: true,
    });
    if (Object.keys(errors).length > 0) return;
    setAnalyzing(true);
    setResult(null);
    const animPromise = playAnalyzeAnimation();
    try {
      await saveIdf(file);
      saveSettingsSlice({ pendingAction: null });
      const data = await analyzePublicIdf({
        file,
        type,
        climate,
        terrain,
        singleBlockPerFloor,
        version: '4.x.x',
      });
      await animPromise;
      setAnalyzePct(100);
      setAnalyzeStepIdx(ANALYZE_STEPS.length);
      setResult(data);
      const autoSkip = data.success ? skipOverwriteFromDiagnosis(data) : true;
      saveSettingsSlice({
        skip_overwrite: autoSkip,
        pendingAction: null,
        analyzeSummary: data.summary
          ? { ...data.summary, timeImpact: data.timeImpact || undefined }
          : null,
      });
      if (!data.success) {
        const convertErrors = data.convertErrors as
          | {
              hint?: string;
              CONVERTER_NOT_CONFIGURED?: string;
              CONVERTER_REMOTE_FAILED?: string;
            }
          | null;
        const convertHint =
          convertErrors?.hint ||
          convertErrors?.CONVERTER_NOT_CONFIGURED ||
          (typeof convertErrors?.CONVERTER_REMOTE_FAILED === 'string'
            ? convertErrors.CONVERTER_REMOTE_FAILED
            : '');
        enqueueSnackbar(
          typeof convertHint === 'string' && convertHint
            ? convertHint
            : t('PUBLIC_ANALYZE_FAILED'),
          { variant: 'error' }
        );
      }
    } catch (error) {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
    } finally {
      // Let the fake progress finish if the API returned early; ignore if already done.
      await animPromise;
      setAnalyzing(false);
    }
  };

  const handleRun = async () => {
    if (!file || !result?.success) {
      enqueueSnackbar(t('PUBLIC_ANALYZE_NEED_ANALYZE'), { variant: 'warning' });
      return;
    }
    const finalSkip = skipOverwriteFromDiagnosis(result);
    try {
      await saveIdf(file);
    } catch {
      enqueueSnackbar(t('PUBLIC_ANALYZE_DRAFT_SAVE_FAILED'), { variant: 'error' });
      return;
    }
    saveSettingsSlice({
      skip_overwrite: finalSkip,
      pendingAction: 'createAndRun',
      analyzeSummary: result.summary
        ? { ...result.summary, timeImpact: result.timeImpact || undefined }
        : null,
    });
    if (isAuthenticated) {
      navigate('/try/resume');
    } else {
      navigate('/login?redirect=/try/resume');
    }
  };

  const mismatchCount = result?.summary?.mismatches ?? 0;
  const parseErrors = result?.parseErrors;
  const parseErrorCount = Array.isArray(parseErrors)
    ? parseErrors.filter(Boolean).length
    : parseErrors && typeof parseErrors === 'object'
      ? Object.keys(parseErrors).filter(Boolean).length
      : 0;
  // Types = distinct sections with a hard mismatch (same role as diagnosis category bars).
  const typesCount = (() => {
    const sections = result?.mismatchesBySection || [];
    let n = 0;
    for (const group of sections) {
      const rows = Array.isArray(group.mismatches) ? group.mismatches : [];
      const hasHard = rows.some((row) =>
        isHardMismatch({
          section: (row.section as string | undefined) || group.section,
          field: row.field as string | undefined,
          isRed: row.isRed as boolean | undefined,
          hasNullCodeValue: row.hasNullCodeValue as boolean | undefined,
          severityType: row.severityType as 'null' | 'orange' | 'red' | undefined,
          codeValue: row.codeValue as string | null | undefined,
        })
      );
      if (hasHard) n += 1;
    }
    return n;
  })();
  const isClean = result?.success && mismatchCount === 0;

  const beforeH = formatHours(result?.timeImpact?.beforeUploadMinutes || 0);
  const afterH = formatHours(result?.timeImpact?.afterUploadMinutes || 0);
  const totalH = formatHours(result?.timeImpact?.totalImpactMinutes || 0);
  const beforeCost = beforeH * hourlyRate;
  const afterCost = afterH * hourlyRate;
  const totalCost = totalH * hourlyRate;
  const ratePct = ((hourlyRate - RATE_MIN) / (RATE_MAX - RATE_MIN)) * 100;

  const showDiagnosis = !!result?.success && !analyzing;
  const showSetup = !analyzing && !showDiagnosis;

  return (
    <div className="public-analyze">
      {showSetup && (
        <section className="public-analyze__panel">
          <p className="public-analyze__lead">{t('PUBLIC_ANALYZE_LEAD')}</p>

          <div className="public-analyze__fields">
            <PaSelect
              label={t('PUBLIC_ANALYZE_TYPE')}
              value={type}
              error={fieldError('type')}
              options={type_options.map((o) => ({
                value: o.value,
                label: t(o.label),
              }))}
              onChange={(v) => {
                void formik.setFieldValue('type', v);
                saveSettingsSlice({ type: v });
              }}
            />

            <PaSelect
              label={t('PUBLIC_ANALYZE_CLIMATE')}
              value={climate}
              placeholder="—"
              error={fieldError('climate')}
              options={climate_v3_options.map((o) => ({
                value: o.value,
                label: t(o.label),
              }))}
              onChange={(v) => {
                void formik.setFieldValue('climate', v);
                saveSettingsSlice({ climate: v });
              }}
            />

            <PaSelect
              label={t('PUBLIC_ANALYZE_TERRAIN')}
              value={terrain}
              error={fieldError('terrain')}
              options={terrain_v3_options.map((o) => ({
                value: o.value,
                label: t(o.label),
              }))}
              onChange={(v) => {
                void formik.setFieldValue('terrain', v);
                saveSettingsSlice({ terrain: v });
              }}
            />

            <PaSelect
              label={t('PUBLIC_ANALYZE_NAMING')}
              value={singleBlockPerFloor ? 'floor' : 'unit'}
              options={[
                { value: 'floor', label: t('PUBLIC_ANALYZE_NAMING_FLOOR') },
                { value: 'unit', label: t('PUBLIC_ANALYZE_NAMING_UNIT') },
              ]}
              onChange={(v) => {
                const floor = v === 'floor';
                void formik.setFieldValue('singleBlockPerFloor', floor);
                saveSettingsSlice({ single_block_per_floor: floor });
              }}
            />
          </div>

          <div className="public-analyze__drop-wrap">
            <IdfFileDropzone
              file={file}
              fullWidth
              onFileChange={(files) => {
                void acceptFile(files[0] || null);
              }}
              onClear={clearFile}
              disabled={analyzing}
              isFileMissing={fileMissing}
            />
          </div>

          <button
            type="button"
            className="public-analyze__analyze-btn"
            disabled={!file || analyzing}
            onClick={() => void handleAnalyze()}
          >
            {t('PUBLIC_ANALYZE_ANALYZE')}
          </button>
        </section>
      )}

      {analyzing && (
        <section className="public-analyze__panel public-analyze__panel--plain">
          <div className="public-analyze__overlay">
            <div className="public-analyze__spinner" />
            <h3>{t('PUBLIC_ANALYZE_ANALYZING')}</h3>
            <div className="public-analyze__bar">
              <div
                className="public-analyze__bar-fill"
                style={{ width: `${analyzePct}%` }}
              />
            </div>
            <div className="public-analyze__pct">{analyzePct}%</div>
            <ul className="public-analyze__steps">
              {ANALYZE_STEPS.map((key, i) => (
                <li
                  key={key}
                  className={
                    i < analyzeStepIdx
                      ? 'done'
                      : i === analyzeStepIdx
                        ? 'active'
                        : ''
                  }
                >
                  <span className="dot" />
                  {t(key)}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {showDiagnosis && (
        <section className="public-analyze__diag">
          <div className={`public-analyze__score${isClean ? ' is-clean' : ''}`}>
            <div className="public-analyze__score-icon">
              {isClean ? (
                <TaskOutlinedIcon sx={{ fontSize: 76 }} />
              ) : (
                <WarningAmberRoundedIcon sx={{ fontSize: 76 }} />
              )}
            </div>
            <div className="public-analyze__score-col">
              <span>{t('IDF_LAB_MISMATCHES')}</span>
              <strong>{mismatchCount.toLocaleString('en-US')}</strong>
            </div>
            <div className="public-analyze__score-col">
              <span>{t('IDF_LAB_TYPES')}</span>
              <strong>{typesCount.toLocaleString('en-US')}</strong>
            </div>
            <div className="public-analyze__score-col">
              <span>{t('IDF_LAB_PARSE_ERRORS')}</span>
              <strong>{parseErrorCount.toLocaleString('en-US')}</strong>
            </div>
          </div>

          {isClean ? (
            <p className="public-analyze__score-note is-clean">
              {t('PUBLIC_ANALYZE_NO_MISMATCHES')}
            </p>
          ) : (
            <p className="public-analyze__score-note is-mismatch">
              {t('PUBLIC_ANALYZE_MISMATCH_NOTE')}
            </p>
          )}

          <div className={`public-analyze__impact${isClean ? '' : ' has-mismatch'}`}>
            <div className="public-analyze__impact-col">
              <Wrench aria-hidden strokeWidth={2} className="public-analyze__impact-ico" />
              <div className="public-analyze__impact-body">
                <span className="label">{t('PUBLIC_ANALYZE_IMPACT_FIX')}</span>
                <IlsAmount className="cost" value={beforeCost} />
                <span className="hours">
                  {t('PUBLIC_ANALYZE_HOURS', { hours: beforeH })}
                </span>
              </div>
            </div>
            <span className="public-analyze__impact-op">+</span>
            <div className="public-analyze__impact-col public-analyze__impact-col--sim">
              <Hourglass
                aria-hidden
                strokeWidth={2}
                className="public-analyze__impact-ico public-analyze__icon-sim"
              />
              <div className="public-analyze__impact-body">
                <span className="label">{t('PUBLIC_ANALYZE_IMPACT_SIM')}</span>
                <IlsAmount className="cost" value={afterCost} />
                <span className="hours">
                  {t('PUBLIC_ANALYZE_HOURS', { hours: afterH })}
                </span>
              </div>
            </div>
            <span className="public-analyze__impact-op">=</span>
            <div className="public-analyze__impact-col">
              <Coins aria-hidden strokeWidth={2} className="public-analyze__impact-ico" />
              <div className="public-analyze__impact-body">
                <span className="label">{t('PUBLIC_ANALYZE_IMPACT_TOTAL')}</span>
                <IlsAmount className="cost" value={totalCost} />
                <span className="hours">
                  {t('PUBLIC_ANALYZE_HOURS', { hours: totalH })}
                </span>
              </div>
            </div>
          </div>

          <div className="public-analyze__actions">
            <div
              className={`public-analyze__rate${isClean ? '' : ' has-mismatch'}`}
              style={{ ['--rate-pct' as string]: `${ratePct}%` }}
            >
              <div className="public-analyze__rate-body">
                <div className="public-analyze__rate-head">
                  <Clock aria-hidden strokeWidth={2} />
                  {t('PUBLIC_ANALYZE_HOURLY_RATE')}
                </div>
                <div className="public-analyze__rate-value">
                  <IlsAmount value={hourlyRate} />
                </div>
                <input
                  type="range"
                  min={RATE_MIN}
                  max={RATE_MAX}
                  step={10}
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(Number(e.target.value))}
                />
              </div>
              <div className="public-analyze__rate-hint">
                <span className="hint-title">{t('PUBLIC_ANALYZE_SAVINGS')}</span>
                <span className="hint-values">
                  <span className="hint-hours">
                    {t('PUBLIC_ANALYZE_HOURS', { hours: totalH })}
                  </span>
                  <span className="hint-sep" aria-hidden />
                  <IlsAmount className="hint-cost" value={totalCost} />
                </span>
              </div>
            </div>

            <div className={`public-analyze__cta${isClean ? ' is-ready' : ''}`}>
              <div className="public-analyze__cta-label">
                {isClean ? (
                  <Play aria-hidden strokeWidth={2} className="public-analyze__icon-play" />
                ) : (
                  <Sparkles aria-hidden strokeWidth={2} />
                )}
                {isClean
                  ? t('PUBLIC_ANALYZE_CTA_LABEL')
                  : t('PUBLIC_ANALYZE_CTA_AUTOFIX')}
              </div>
              <ul className="public-analyze__checklist">
                <li>
                  <CircleCheck aria-hidden strokeWidth={2} className="public-analyze__check-ico" />
                  {isClean
                    ? t('PUBLIC_ANALYZE_CTA_CHECK_INPUTS')
                    : t('PUBLIC_ANALYZE_CTA_CHECK_ALIGN')}
                </li>
                <li>
                  <CircleCheck aria-hidden strokeWidth={2} className="public-analyze__check-ico" />
                  {t('PUBLIC_ANALYZE_CTA_CHECK_SIM')}
                </li>
                <li>
                  <CircleCheck aria-hidden strokeWidth={2} className="public-analyze__check-ico" />
                  {t('PUBLIC_ANALYZE_CTA_CHECK_BASELINES')}
                </li>
              </ul>
              <button
                type="button"
                className="public-analyze__cta-btn"
                onClick={() => void handleRun()}
              >
                {isClean ? t('PUBLIC_ANALYZE_RUN') : t('PUBLIC_ANALYZE_RUN_FIX')}
              </button>
            </div>
          </div>
        </section>
      )}

    </div>
  );
};

export default PublicAnalyzePage;
